import { IconButton, Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/react';
import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import MoreIcon from '@/assets/icons/ic-more.svg';

import { IToastType } from '@/enums/ToastType';
import { destroyComment, queryLectureCommentsInfinite } from '@/fetchers/comments';
import useSession from '@/hooks/useSession';
import { useStores } from '@/hooks/useStores';
import { useToast } from '@/hooks/useToast';
import { useMutation, useResourcesInfinite } from '@/lib/datx-jsonapi-react';
import { Comment } from '@/resources/Comment';

export interface ICommentMenuProps {
  comment?: Comment;
  threadId?: string;
  lectureId?: string;
  onDelete?: (threadId: string) => void;
}

export const CommentMenu: FC<ICommentMenuProps> = ({ threadId, lectureId, comment, onDelete }) => {
  const { t } = useTranslation();
  const { user } = useSession();
  const { store } = useStores();
  const toast = useToast();

  const { mutate } = useResourcesInfinite(queryLectureCommentsInfinite({ threadId, lectureId }));

  const [destroy, { status }] = useMutation(destroyComment, {
    onSuccess: async () => {
      await mutate();

      onDelete?.(threadId);
      toast({ status: IToastType.SUCCESS, title: t('comments.toast.success') });
    },
    onFailure: () => {
      toast({ status: IToastType.ERROR, title: t('comments.toast.failure') });
    },
  });

  const isOwner = comment?.user?.id === user?.id;
  const isAdmin = user?.role === 'admin';

  const isDisabled = !(user && (isOwner || isAdmin));

  const onDeleteComment = () => {
    destroy({ store, comment });
  };

  if (isDisabled) {
    return null;
  }

  return (
    <Menu variant="navigation" placement="bottom-end">
      {({ isOpen }) => (
        <Fragment>
          <MenuButton
            as={IconButton}
            aria-label="Options"
            icon={<MoreIcon id="moreIcon" data-visible={isOpen} />}
            size="xs"
            variant="ghost"
            isLoading={status === 'running'}
          />
          <MenuList minW="200px">
            <MenuItem as="button" type="button" onClick={onDeleteComment}>
              {t('comments.deleteComment.label')}
            </MenuItem>
          </MenuList>
        </Fragment>
      )}
    </Menu>
  );
};
